import { useState } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const faqData = [
  {
    category: 'Troubleshooting',
    items: [
      {
        question: "My heating or cooling isn't working - what should I do first?",
        answer: `1. Check Power & Settings:
• Ensure controller has power and correct mode (heating/cooling)
• Replace batteries if applicable
• Check that circuit breaker and outdoor isolator switch are ON

2. Maintenance Check:
• Clean filters (this should be done monthly)
• Try a power reset: for gas ducted heating and evaporative cooling, turn the MAIN power switch OFF in the meter box for 10 minutes. For split, multi split and reverse cycle ducted systems, turn the isolator switch next to the outdoor unit OFF for 10 minutes
• Set temperature 2-3 degrees above/below room temperature depending on mode

3. If still not working:
• Refer to your operation manual
• Contact us if you need a manual copy`
      },
      {
        question: "How do I know if the issue is with heating or cooling?",
        answer: `1. Check Controller:
• Look at the display mode (Cooling or Heating)
• Check if system is producing warm/cool air
• Verify airflow from vents
• Look for any error codes or unusual displays`
      },
      {
        question: "There's no air coming from the vents - what could be wrong?",
        answer: `1. Basic Checks:
• Confirm system is on and settings are correct
• Ensure all vents/registers are open
• Allow 5 minutes for system to ramp up

2. Airflow Check:
• Listen at return air grille - whistling indicates dirty filter
• Verify all zones are switched ON`
      },
      {
        question: "Some vents are blowing air, but others aren't. Why?",
        answer: `Common Causes:
• Zone settings may have some zones turned off
• Possible ductwork issues (disconnected/collapsed ducts)
• Outlet dampers might be closed
• Return air (RA) filter needs cleaning`
      },
      {
        question: "My controller has no display - what now?",
        answer: `Follow these steps:
1. Check Power Sources:
• Replace batteries if applicable
• Verify roof power point is ON
• Ensure meter box switch and isolator are ON

2. If still no display:
• Check manual for error codes
• Contact support if issue persists`
      },
      {
        question: "My system shows an error code - what does it mean?",
        answer: `Important Steps:
1. Note down the exact error code
2. Check your owner's manual or manufacturer's website
3. Include error code when submitting service/warranty requests

Note: Each brand uses different error codes - specific solutions vary by manufacturer`
      },
      {
        question: "My remote/controller is unresponsive - can I reset it myself?",
        answer: `Yes! Try these steps:
1. Battery Reset:
• Remove batteries for 30 seconds
• Install new batteries (avoid rechargeable ones)
• Press reset button if available

2. For Ducted Systems:
• Gas ducted heating and evaporative cooling: turn the MAIN power switch OFF in the meter box for 10 minutes, then back ON
• Reverse cycle ducted: turn the isolator switch next to the outdoor unit OFF for 10 minutes, then back ON`
      },
      {
        question: "What if I've had an electrical or gas outage?",
        answer: `Recovery Steps:
1. Perform Manual Reset:
• Gas ducted heating and evaporative cooling: turn OFF the MAIN power switch in the meter box for 10 minutes, then turn it back ON
• Split, multi split and reverse cycle ducted: turn OFF the isolator switch next to the outdoor unit for 10 minutes, then turn it back ON
2. Verify all breakers are in ON position
3. Check controller for any error messages`
      },
      {
        question: "My system turns off after a few minutes - what should I check?",
        answer: `Common Causes & Solutions:
1. Airflow Issues:
• Clean dirty filters
• Clear blocked return air grille
• Remove furniture/rugs blocking vents

2. Temperature Issues:
• Move thermostat away from heat sources
• Ensure all vents are open throughout property`
      },
      {
        question: "My split system is dripping water inside - is that normal?",
        answer: `No, indoor dripping indicates:
1. Possible Causes:
• Blocked condensate drain
• Incorrect installation
• High humidity conditions

2. Action Steps:
• Turn off system
• Clean filters
• Check drain tray and outdoor drain
• Verify drains aren't blocked by landscaping/debris
• Book technician if issue persists`
      },
      {
        question: "My system takes too long to heat/cool - how can I speed it up?",
        answer: `Optimisation Tips:
1. Environment Control:
• Close all doors/windows (except for evaporative systems)
• Set 20-22°C for heating or 24-26°C for cooling. Setting it higher or lower does not heat or cool faster
• Avoid extreme settings

2. Operation:
• Use HEAT or COOL mode (FAN mode only moves air)
• Allow system to stabilise
• Avoid frequent setting changes`
      },
      {
        question: "My zones don't respond - how do I check them?",
        answer: `Troubleshooting Steps:
1. Check Controller:
• Verify each zone is enabled
• Look for greyed out zones

2. Physical Checks:
• Ensure outlets aren't manually closed
• Try power cycling if zones are unresponsive`
      },
      {
        question: "Need Help with Your MyAir Controller?",
        answer: `If you need assistance with setting up or using your MyAir controller:

1. Contact Support:
• Call MyAir Customer Care: 1300 850 191
• They can assist with WiFi setup and system operation

2. Video Guides:
• Visit [MyAir Video Guides](https://www.advantageair.com.au/faqs/support-faq/help-videos/) for helpful tutorials`
      }
    ]
  },
  {
    category: 'System Care & Usage',
    items: [
      {
        question: "What are common reasons my system stops working?",
        answer: `Common Issues:
1. Maintenance Related:
• Dirty filters blocking airflow
• Incorrect controller settings
• Power issues (roof point/power boards)

2. System Setup:
• Zones turned off (ducted systems)
• Manually closed outlets
• Closed windows (evaporative only)`
      },
      {
        question: "Do I need to open windows for evaporative cooling to work?",
        answer: `Yes - Essential Steps:
1. Window Management:
• Open a window approx. 20cm in every room an evaporative cooling outlet is installed
• Especially important when doors are closed
• Wider opening needed for higher fan speeds

2. Important:
• Open windows BEFORE starting system
• Prevents pressure buildup
• Allows proper air circulation`
      },
      {
        question: "How do I clean my system's filters?",
        answer: `Maintenance Schedule:
• Clean filters monthly (more often in dusty conditions)
• Follow manufacturer's specific instructions
• Refer to unit manual for detailed cleaning steps`
      },
      {
        question: "Do I need to service my evaporative system regularly?",
        answer: `Yes - Maintenance Schedule:
1. Regular Care:
• Clean filters monthly (more often in dusty conditions)
• Schedule professional service annually

2. Timing:
• Book annual service before summer season
• Ensures optimal performance when needed most`
      },
      {
        question: "What if another trade has been in the roof recently?",
        answer: `Potential Issues:
• System may be unplugged
• Ducting could be damaged
• Wiring might be disconnected

Action Steps:
• Contact recent trades
• Let us know if issues persist`
      },
      {
        question: "The air smells musty or dusty - what should I do?",
        answer: `Resolution Steps:
1. Immediate Actions:
• Clean filters thoroughly
• Follow brand-specific cleaning guide

2. If Smell Persists:
• Book service (especially for systems 3+ years old)
• May need professional coil cleaning`
      },
      {
        question: "Can I run heating and cooling at the same time in different rooms?",
        answer: `Ducted and multi split systems: No
• All zones or indoor units must run in the same mode (all heating or all cooling)
• You cannot mix modes in different areas

Separate single split systems: Yes
• Each unit runs on its own`
      },
      {
        question: "Is it cheaper to leave the system running all day or turn it off?",
        answer: `Energy Efficient Operation:
1. Best Practice:
• Use timer to start 30 minutes before needed
• Don't run continuously all day
• Program based on your routine

2. For Evaporative Cooling:
• Start early while outside air is cool
• Easier to maintain temperature than cool hot house
• More energy efficient approach`
      }
    ]
  },
  {
    category: 'Service & Support',
    items: [
      {
        question: "For booking general service - what do I need to know?",
        answer: `If issue is with the unit (not install), visit these links:
• [ActronAir: Service & Warranty](https://www.actronair.com.au/support/service-warranty/)
• [Rinnai: Online Service Booking](https://www.rinnai.com.au/support-resources/online-service-booking/)
• [Mitsubishi Electric: Service Request](https://www.mitsubishielectric.com.au/service-request/)
• [Fujitsu General: Warranty Information](https://www.fujitsugeneral.com.au/help-centre/warranty-information)
• [Daikin: Raise a Service Request](https://www.daikin.com.au/raise-service-request)
• [Seeley International (Braemar/Breezair/Coolair): Warranty Info](https://www.seeleyinternational.com/support/warranty-information/)
• [Bonaire: Book a Service](https://www.bonaire.com.au/pages/book-service)
• [Haier / Fisher & Paykel: Warranty Info](https://www.haier.com.au/help-and-support/warranty-information)
• [Panasonic: Support](https://www.panasonic.com/au/support.html)
• [Advantage Air (MyAir / e-zone): Support Centre](https://support.advantageair.com.au/)
• [AirTouch: Homeowner Support](https://help.airtouch.net.au/get-in-touch)

Please have ready:
• Model/serial number
• Installation date
• Detailed issue description`
      },
      {
        question: "Where can I find my controller manual?",
        answer: `Manufacturer Links:
• [ActronAir LM7/LM24 Wall Controller](https://www.actronair.com.au/wp-content/uploads/2021/03/LM7-LM24-3009-1.pdf)
• [ActronAir Serene 2 Remote Controller](https://www.actronair.com.au/wp-content/uploads/2020/11/9590-4010-Serene-2-Remote-Control-Owners-Manual.pdf)
• [Rinnai Hi-Wall Split](https://www.rinnai.com.au/support/products)
• [Mitsubishi Remote Guide](https://www.mitsubishielectric.com.au/blog/master-your-air-conditioners-remote/), [Remote Control Symbols](https://www.mitsubishielectric.com.au/homeowner/support/remote-control-symbols/)
• [Fujitsu Remote](https://www.fujitsugeneral.com.au/help-centre/helpful-articles/details/how-to-use-your-fujitsu-remote-controller), [Setting the Timer Function](https://www.fujitsugeneral.com.au/help-centre/helpful-articles/details/how-to-set-your-air-conditioner-s-timer-function-for-maximum-home-comfort-and-energy-efficiency)
• [Daikin Controller Manual](https://www.daikin.com.au/_files/ugd/7c0c0c_778cfd2d11bd48fcb48d7386f0600ea8.pdf), [Controller Symbols FAQ](https://www.daikin.com.au/faq/what-do-symbols-my-controller-mean)
• [Haier Wired Remote](https://www.haier.com.au/on/demandware.static/-/Sites-haier-master-catalog/default/dw7c095851/InstallationManuals-HaierAU/YR-E17-User_Manual_-_AA0K10E2900.pdf), [Hi-Wall Guide](https://www.haier.com.au/on/demandware.static/-/Sites-haier-master-catalog/default/dw44334363/technical-content/airconditioning/hi-wall-split/haier-tempo-aircon-operation-manual-AS-models.pdf)
• [Braemar YAP1F](https://www.seeleyinternational.com/artefact/braemar-reverse-cycle-yap1f-controller-owner-manual-english/), [EasySet](https://www.seeleyinternational.com/artefact/braemar-easyset-controller-manual)
• [Braemar MagIQtouch Info](https://www.seeleyinternational.com/magiqtouch/), [Owner's Manual](https://www.seeleyinternational.com/artefact/magiqtouch-controller-owner-manual-english/)
• [Bonaire Navigator Owner's Manual](https://cdn.shopify.com/s/files/1/0591/5682/4230/files/Bonaire_Navigator_Controller_-_Owners_Manual.pdf)
• [MyAir User Manual](https://www.advantageair.com.au/wp-content/uploads/2026/10/MyAir-and-MyAir-User-Manual-v4.1b.pdf)

Can't find yours? Contact manufacturer support.`
      }
    ]
  }
];

const convertMarkdownLinks = (text: string) => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the link
    parts.push(
      <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer">
        {match[1]}
      </a>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const FAQ = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, 
        width: '100%',
        maxWidth: { xs: '100%', sm: '100%', md: 900 },
        mx: 'auto',
        boxSizing: 'border-box'
      }}
    >
      {faqData.map((category, categoryIndex) => (
        <div key={categoryIndex}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 2, 
              mt: categoryIndex > 0 ? 4 : 0,
              fontWeight: 600,
              color: 'primary.main',
              fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.6rem' }
            }}
          >
            {category.category}
          </Typography>
          {category.items.map((item, itemIndex) => (
            <Accordion
              key={itemIndex}
              expanded={expanded === `${categoryIndex}-${itemIndex}`}
              onChange={handleChange(`${categoryIndex}-${itemIndex}`)}
              sx={{
                mb: 1,
                boxShadow: 'none',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: '0 0 10px 0',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
                borderRadius: '8px !important',
                border: '1px solid',
                borderColor: 'divider',
                width: '100%'
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: '48px',
                  '& .MuiAccordionSummary-content': {
                    margin: '10px 0',
                  },
                  '&.Mui-expanded': {
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                  }
                }}
              >
                <Typography 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    color: expanded === `${categoryIndex}-${itemIndex}` ? 'primary.main' : 'text.primary',
                  }}
                >
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2, pb: 2, px: { xs: 2, sm: 3 } }}>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: 'pre-line',
                    '& a': {
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    },
                    '& > *': {
                      marginBottom: '6px',
                    },
                    '& ul, & ol': {
                      paddingLeft: { xs: '20px', sm: '25px' },
                      marginTop: '4px',
                      marginBottom: '4px'
                    },
                    '& li': {
                      marginBottom: '4px',
                      paddingRight: '4px'
                    },
                    lineHeight: 1.5,
                    color: 'text.secondary',
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    paddingLeft: { xs: 0, sm: 1 },
                    '& > div': {
                      maxWidth: '100%'
                    }
                  }}
                >
                  {convertMarkdownLinks(item.answer)}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
          {categoryIndex < faqData.length - 1 && (
            <Divider 
              sx={{ 
                mt: 3,
                mb: 2,
                opacity: 0.6 
              }} 
            />
          )}
        </div>
      ))}
    </Paper>
  );
};

export default FAQ;